import {
  CopyOutlined,
  DeleteOutlined,
  PlusOutlined,
} from '@ant-design/icons';
import { FileFormatter } from '@features/products/interfaces/offerBulkImport';
import { Button, Input, Space, Table, Tooltip, Typography } from 'antd';
import { useMemo } from 'react';
import styled from 'styled-components';

export type EditableOfferRow = {
  id: string;
  values: FileFormatter;
};

type EditableOffersPreviewTableProps = {
  rows: EditableOfferRow[];
  columnsKeys: string[];
  onCellChange: (rowId: string, field: string, value: string) => void;
  onAddRow: () => void;
  onRemoveRow: (rowId: string) => void;
  onDuplicateRow: (rowId: string) => void;
};

export function EditableOffersPreviewTable({
  rows,
  columnsKeys,
  onCellChange,
  onAddRow,
  onRemoveRow,
  onDuplicateRow,
}: EditableOffersPreviewTableProps) {
  const tableColumns = useMemo(() => {
    const indexColumn = {
      title: '#',
      dataIndex: '__index__',
      key: '__index__',
      width: 64,
      fixed: 'left' as const,
    };

    const dataColumns = columnsKeys.map(key => ({
      title: key,
      dataIndex: key,
      key,
      width: 180,
      render: (value: string, record: { key: string }) => (
        <Input
          aria-label={`${key} da oferta ${record.key}`}
          value={value ?? ''}
          onChange={event => onCellChange(record.key, key, event.target.value)}
        />
      ),
    }));

    const actionsColumn = {
      title: 'Ações',
      key: 'actions',
      width: 112,
      fixed: 'right' as const,
      render: (_: unknown, record: { key: string; __index__: number }) => (
        <Space>
          <Tooltip title='Duplicar linha'>
            <Button
              aria-label={`Duplicar linha ${record.__index__}`}
              icon={<CopyOutlined />}
              onClick={() => onDuplicateRow(record.key)}
            />
          </Tooltip>
          <Tooltip title='Remover linha'>
            <Button
              danger
              aria-label={`Remover linha ${record.__index__}`}
              icon={<DeleteOutlined />}
              onClick={() => onRemoveRow(record.key)}
            />
          </Tooltip>
        </Space>
      ),
    };

    return [indexColumn, ...dataColumns, actionsColumn];
  }, [columnsKeys, onCellChange, onDuplicateRow, onRemoveRow]);

  const dataSource = useMemo(
    () =>
      rows.map((row, index) => ({
        key: row.id,
        __index__: index + 1,
        ...row.values,
      })),
    [rows]
  );

  return (
    <PreviewTableSection>
      <HeaderContent>
        <div>
          <Typography.Title level={5} style={{ margin: 0 }}>
            Pré-visualização das ofertas
          </Typography.Title>
          <Typography.Text type='secondary'>
            Apenas colunas válidas e mapeadas serão salvas.
          </Typography.Text>
        </div>
        <Button icon={<PlusOutlined />} onClick={onAddRow}>
          Adicionar linha
        </Button>
      </HeaderContent>
      <PreviewTableWrapper>
        <Table
          size='small'
          bordered
          pagination={false}
          columns={tableColumns}
          dataSource={dataSource}
          scroll={{ x: 'max-content', y: 280 }}
        />
      </PreviewTableWrapper>
    </PreviewTableSection>
  );
}

const HeaderContent = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
`;

const PreviewTableSection = styled.section`
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 12px;
  border: 1px solid rgba(255, 255, 255, 0.12);
  border-radius: 8px;
  background: rgba(255, 255, 255, 0.04);

  .ant-typography {
    color: rgba(255, 255, 255, 0.92);
  }

  .ant-typography-secondary {
    color: rgba(255, 255, 255, 0.7);
  }
`;

const PreviewTableWrapper = styled.div`
  width: 100%;
  overflow: hidden;
  border-radius: 6px;

  .ant-table-wrapper {
    width: 100%;
  }
`;
